<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application?.localName || application?.title || ''"
      role="navigation"
    />
    <TitleAndDescription
      :application="application"
      :local-title="
        $t('titles.data-types-repository', {
          dataName: application?.localRefName || dataId,
        })
      "
      :local-description="application?.localRefDescription"
      :local-name="application?.localRefName"
    />
    <section v-if="hasAuthorizationScopes && isWithoutReferenceScopes">
      <b-message
        :title="$t('message.referenceScope-is-empty.title')"
        class="mt-4"
        has-icon
        type="is-danger"
      >
        <span
          class="column"
          v-html="$t('message.referenceScope-is-empty.message', {names: listReferenceScopeName.join(', ')})"
        />
      </b-message>
      <div class="column is-offset-5">
        <CardAccessSectionApplication
          :param-btn-click="application"
          :title-card="$t('applications.references')"
          btn-function-name="displayReferencesManagement"
          btn-icon="drafting-compass"
          btn-type="is-dark"
          :description-card="$t('applications.references_description')"
        />
      </div>
    </section>
    <div
      v-else
      class="contentView"
    >
      <div class="columns">
        <div class="column">
          <LoadingAnimate
            v-if="isLoading"
            :size="'is-large'"
          />
          <form
            v-else
            class="card no-padding"
          >
            <b-collapse
              animation="slide"
              aria-id="fileDeposit"
            >
              <template #trigger="props">
                <div
                  aria-controls="fileDeposit"
                  class="card-header"
                >
                  <h2
                    class="card-header-title"
                    :aria-label="$t('dataTypesRepository.card-title-upload-file')"
                  >
                    {{ $t("dataTypesRepository.card-title-upload-file") }}
                  </h2>
                  <a
                    class="card-header-icon"
                    tabindex="0"
                  >
                    <b-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
                  </a>
                </div>
              </template>
              <div class="card-content minHeightStandard">
                <div class="content">
                  <b-steps
                    v-model="activeStep"
                    :has-navigation="true"
                    size="is-medium"
                  >
                    <b-step-item
                      v-if="hasAuthorizationScopes"
                      icon="magnifying-glass-plus"
                      :label="$t('dataVersioning.step-scope')"
                      :aria-label="$t('dataVersioning.step-scope')"
                      step="1"
                    >
                      <div
                        v-for="(referenceScopesByData, index) in referenceScopes[dataId]"
                        :key="index"
                        class="column"
                      >
                        <b-field
                          :aria-label="
                            locale ? (referenceScopesByData.i18n.title[locale] ||
                              referenceScopesByData.i18n[locale]) :
                            referenceScopesByData.id
                          "
                          :label="
                            locale ? (referenceScopesByData.i18n.title[locale] ||
                              referenceScopesByData.i18n[locale]) :
                            referenceScopesByData.id
                          "
                          class="column"
                        >
                          <b-dropdown
                            :id="referenceScopesByData.id"
                            :ref="referenceScopesByData.id"
                            :triggers="['focus']"
                            expanded
                            max-height="20rem"
                            open-on-focus
                            scrollable
                            width="31rem"
                          >
                            <template #trigger="{ active }">
                              <b-taginput
                                v-model="requiredAuthorizationsLabels[referenceScopesByData.id]"
                                :aria-placeholder="
                                  $t('dataTypesRepository.placeholder-select-scope-depot', {
                                    scope:
                                      locale ? (referenceScopesByData.i18n.title[locale] ||
                                        referenceScopesByData.i18n[locale]) :
                                      referenceScopesByData.id,
                                  })
                                "
                                :closable="false"
                                :icon-right="active ? 'angle-up' : 'angle-down'"
                                :placeholder="
                                  $t('dataTypesRepository.placeholder-select-scope-depot', {
                                    scope:
                                      locale ? (referenceScopesByData.i18n.title[locale] ||
                                        referenceScopesByData.i18n[locale]) :
                                      referenceScopesByData.id,
                                  })
                                "
                                rounded
                                type="is-dark"
                                @update:model-value="requiredAuthorizationsLabels[referenceScopesByData.id]"
                              />
                            </template>
                            <CollapseMenuOneSelect
                              v-for="(option, optionKey) in referenceScopesByData.nodes"
                              :id="option.id"
                              :key="optionKey"
                              :id-reference-scope="referenceScopesByData.id"
                              :list-select="requiredAuthorizationsLabels[referenceScopesByData.id]"
                              :option="option"
                              @select-menu-item="selectAuthorization($event.type, $event)"
                            />
                          </b-dropdown>
                        </b-field>
                      </div>
                    </b-step-item>
                    <b-step-item
                      v-if="hasTimeScope"
                      icon="calendar-plus"
                      :label="$t('dataVersioning.step-time')"
                      :aria-label="$t('dataVersioning.step-time')"
                      step="2"
                    >
                      <div class="column">
                        <b-field
                          :label="$t('dataTypesRepository.start-date')"
                          :aria-label="$t('dataTypesRepository.start-date')"
                          aria-hidden="false"
                          class="column"
                          data-cy="dateStart"
                        >
                          <DatePatternInput
                            v-if="patternDateTimeScope"
                            :pattern="patternDateTimeScope"
                            aria-hidden="false"
                            type="startDate"
                            @input="updateDate('startDate', $event)"
                          />
                        </b-field>
                      </div>
                      <div class="column">
                        <b-field
                          :label="$t('dataTypesRepository.end-date')"
                          :aria-label="$t('dataTypesRepository.end-date')"
                          aria-hidden="false"
                          class="column"
                          data-cy="dateEnd"
                        >
                          <DatePatternInput
                            v-if="patternDateTimeScope"
                            :pattern="patternDateTimeScope"
                            aria-hidden="false"
                            type="endDate"
                            @input="updateDate('endDate', $event)"
                          />
                        </b-field>
                      </div>
                    </b-step-item>
                    <b-step-item
                      icon="file-circle-plus"
                      :label="$t('dataVersioning.step-file')"
                      :aria-label="$t('dataVersioning.step-file')"
                      step="3"
                    >
                      <div class="column changeDefaultDisplay">
                        <b-upload
                          v-model="file"
                          expanded
                          drag-drop
                          :aria-label="$t('dataTypesRepository.choose-file')"
                          @input="changeFile"
                        >
                          <section>
                            <div class="content has-text-centered">
                              <p>
                                <b-icon
                                  icon="upload"
                                  size="is-large"
                                />
                              </p>
                              <p>{{ $t("dataTypesRepository.choose-file") }}</p>
                            </div>
                          </section>
                        </b-upload>
                      </div>
                      <b-tag
                        v-if="file && file.name"
                        class="column"
                        icon="file-csv"
                        size="is-medium"
                      >
                        {{ file.name }}
                      </b-tag>
                    </b-step-item>
                    <b-step-item
                      icon="check"
                      :label="$t('dataVersioning.step-validate')"
                      :aria-label="$t('dataVersioning.step-validate')"
                      step="4"
                    >
                      <b-field
                        :aria-label="$t('dataTypesRepository.comment')"

                        :label="$t('dataTypesRepository.comment')"
                        class="column"
                        expanded
                      >
                        <b-input
                          v-model="comment"
                          maxlength="200"
                          type="textarea"
                        />
                      </b-field>
                    </b-step-item>
                    <template #navigation="{ previous, next }">
                      <div class="columns">
                        <div class="column is-2">
                          <b-button
                            :disabled="previous.disabled"
                            expanded
                            icon-left="chevron-left"
                            @click.prevent="previous.action"
                          />
                        </div>
                        <div
                          v-if="hasAuthorizationScopes && hasTimeScope"
                          class="column is-offset-8 is-2"
                        >
                          <b-button
                            v-if="activeStep === 0"
                            expanded
                            :disabled="hasAuthorizationScopes &&
                              referenceScopes[dataId]?.length &&
                              Object.keys(requiredAuthorizationsLabels).length !== referenceScopes[dataId].length"
                            icon-right="chevron-right"
                            @click.prevent="next.action"
                          />
                          <b-button
                            v-else-if="activeStep === 1"
                            expanded
                            :disabled="hasTimeScope && (startDate === null || endDate === null)"
                            icon-right="chevron-right"
                            @click.prevent="next.action"
                          />
                          <b-button
                            v-else-if="activeStep === 2"
                            expanded
                            :disabled="file === null"
                            icon-right="chevron-right"
                            @click.prevent="next.action"
                          />
                          <b-button
                            v-else
                            expanded
                            icon-left="floppy-disk"
                            type="is-dark"
                            @click="upload"
                          />
                        </div>
                        <div
                          v-else-if="hasAuthorizationScopes && !hasTimeScope"
                          class="column is-offset-8 is-2"
                        >
                          <b-button
                            v-if="activeStep === 0"
                            expanded
                            :disabled="hasAuthorizationScopes && Object.keys(requiredAuthorizationsLabels).length !== referenceScopes[dataId].length"
                            icon-right="chevron-right"
                            @click.prevent="next.action"
                          />
                          <b-button
                            v-else-if="activeStep === 1"
                            expanded
                            :disabled="file === null"
                            icon-right="chevron-right"
                            @click.prevent="next.action"
                          />
                          <b-button
                            v-else
                            expanded
                            icon-left="floppy-disk"
                            type="is-dark"
                            @click="upload"
                          />
                        </div>
                        <div
                          v-else
                          class="column is-offset-8 is-2"
                        >
                          <b-button
                            v-if="activeStep === 0"
                            expanded
                            :disabled="file === null"
                            icon-right="chevron-right"
                            @click.prevent="next.action"
                          />
                          <b-button
                            v-else
                            :disabled="loading"
                            expanded
                            icon-left="floppy-disk"
                            type="is-dark"
                            @click="upload"
                          />
                        </div>
                      </div>
                    </template>
                  </b-steps>
                </div>
              </div>
            </b-collapse>
          </form>
        </div>
      </div>
      <LoadingAnimate
        v-if="loading"
        :size="'is-large'"
      />
      <div
        v-else-if="(submissionListVisible || !hasAuthorizationScopes) &&
          (referenceScopes[dataId] && Object.keys(requiredAuthorizationsLabels).length === referenceScopes[dataId].length) &&
          !loading &&
          datasets &&
          Object.keys(datasets).length"
        class="columns cardSubmission"
      >
        <div class="column">
          <div
            id="element"
            class="card-content"
          >
            <div
              v-if="!datasets || !Object.keys(datasets).length"
              class="emptyDatasetsState"
              :aria-label="$t('dataTypesRepository.no-data-for-scope')"
            >
              {{ $t("dataTypesRepository.no-data-for-scope") }}
            </div>
            <table
              v-if="datasets && Object.keys(datasets).length"
              class="submissionListVisible table is-striped is-fullwidth numberData"
            >
              <caption class="titleSubmissionListVisible">
                <span
                  class="columns titleSubmission"
                  :aria-label="$t('dataTypesRepository.list-file-data')"
                >
                  {{
                    $t("dataTypesRepository.list-file-data")
                  }}
                </span>
                <span
                  v-if="Object.keys(requiredAuthorizationsLabels || {}).length !== 0"
                  class="columns subtitle titleSubmission"
                  :aria-label="$t('dataTypesRepository.scope-depot') + ': '"
                >
                  <span>
                    {{
                      $t("dataTypesRepository.scope-depot") + ": "
                    }}
                  </span>
                  <b-tag
                    v-for="scopeLabel in Object.values(requiredAuthorizationsLabels || {})"
                    :key="scopeLabel[0]"
                    class="tagScopeSubmission"
                    type="is-primary is-light"
                    size="is-medium"
                    :aria-label="scopeLabel[0]"
                  >
                    {{ scopeLabel[0] }}
                  </b-tag>
                </span>
              </caption>
              <tr>
                <th
                  v-if="hasTimeScope"
                  :aria-label="$t('dataTypesRepository.table-file-data-period')"
                >
                  {{ $t("dataTypesRepository.table-file-data-period") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.version-number')"
                >
                  {{ $t("dataTypesRepository.version-number") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-publication')"
                >
                  {{ $t("dataTypesRepository.table-file-data-publication") }}
                </th>
              </tr>
              <tr
                v-for="[periode, dataset] in paginatedDatasets"
                :key="dataset.id"
                :class="['clickableStyle', { 'is-disabled': loading, 'is-selected-row': periode === selectedPeriode }]"
                :tabindex="loading ? -1 : 0"
                :aria-disabled="loading"
                :aria-selected="periode === selectedPeriode"
                @click="loading ? null : showDatasets(dataset, String(periode))"
                @keyup.enter="loading ? null : showDatasets(dataset, String(periode))"
              >
                <td
                  v-if="hasTimeScope"
                  :aria-label="periode.toString()"
                >
                  {{ periode }}
                </td>
                <td
                  :aria-label="Object.keys(dataset?.datasets).length.toString()"
                >
                  {{ Object.keys(dataset?.datasets).length }}
                </td>
                <td
                  :aria-label="dataset.publication || $t('dataTypesRepository.no-publish')"
                >
                  {{ dataset.publication || $t("dataTypesRepository.no-publish") }}
                </td>
              </tr>
            </table>
            <b-pagination
              v-if="datasets && Object.keys(datasets).length && totalDatasets > perPage"
              v-model="currentPage"
              :total="totalDatasets"
              :per-page="perPage"
              order="is-centered"
              size="is-small"
              rounded
              aria-next-label="Page suivante"
              aria-previous-label="Page précédente"
              aria-page-label="Page"
              aria-current-label="Page actuelle"
            />
            <table
              v-if="currentDataset && currentDataset.length"
              class="submissionListVisible table is-striped is-fullwidth"
            >
              <caption
                class="titleSubmissionListVisible"
                :aria-label="$t('dataTypesRepository.list-file-data-period') + ': '+ currentDataset[0].periode"
              >
                {{
                  $t("dataTypesRepository.list-file-data-period")
                }}
                {{

                  currentDataset[0].periode
                }}
                <div
                  v-if="errorsMessages.length !== 0"
                  style="margin: 10px"
                >
                  <ShowErrors
                    :errors-messages="errorsMessages"
                    :errors-messages-length="errorsMessages.length"
                    :title="$t('message.data-type-config-error')"
                  />
                </div>
              </caption>

              <tr>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-version')"
                >
                  {{ $t("dataTypesRepository.table-file-data-version") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-comment')"
                >
                  {{ $t("dataTypesRepository.table-file-data-comment") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-size')"
                >
                  {{ $t("dataTypesRepository.table-file-data-size") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-create')"
                >
                  {{ $t("dataTypesRepository.table-file-data-create") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-create-by')"
                >
                  {{ $t("dataTypesRepository.table-file-data-create-by") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-publish')"
                >
                  {{ $t("dataTypesRepository.table-file-data-publish") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-publish-by')"
                >
                  {{ $t("dataTypesRepository.table-file-data-publish-by") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-publication')"
                >
                  {{ $t("dataTypesRepository.table-file-data-publication") }}
                </th>
                <th
                  :aria-label="$t('dataTypesRepository.table-file-data-action')"
                >
                  {{ $t("dataTypesRepository.table-file-data-action") }}
                </th>
              </tr>
              <tr
                v-for="(dataset, index) in currentDataset"
                :key="dataset.id"
              >
                <td :aria-label="(index + 1).toString()">
                  {{ index + 1 }}
                </td>
                <td
                  v-if="UTCToString(dataset.params.comment) && UTCToString(dataset.params.comment) !=='null'"
                >
                  <b-tooltip
                    :id="dataset.id"
                    multilined
                    role="tooltip"
                    type="is-dark"
                  >
                    <template #content>
                      <h3 :aria-label="$t('dataTypesRepository.comment')">
                        {{ $t("dataTypesRepository.comment") }} {{ $t("ponctuation.colon") }}
                      </h3>
                      <p :aria-label="UTCToString(dataset.params.comment)">
                        {{ UTCToString(dataset.params.comment) }}
                      </p>
                    </template>
                    <a
                      :aria-describedby="dataset.id"
                      tabindex="0"
                      :aria-label="$t('dataTypesRepository.comment')"
                      @keyup.enter="changeCss(dataset.id)"
                    >
                      <b-icon
                        icon="eye"
                        size="is-small"
                      />
                    </a>
                  </b-tooltip>
                </td>
                <td
                  v-else
                >
                  <p :aria-label="$t('dataVersioning.no-comment')">
                    {{ $t("dataVersioning.no-comment") }}
                  </p>
                </td>
                <td
                  :aria-label="dataset.size / 1000 +': '+ $t('dataTypesRepository.table-file-data-size-unit')"
                >
                  {{ dataset.size / 1000 }}
                  {{ $t("dataTypesRepository.table-file-data-size-unit") }}
                </td>
                <td
                  :aria-label="formatDate(dataset?.params?.createdate)"
                >
                  {{ formatDate(dataset?.params?.createdate) }}
                </td>
                <td
                  :aria-label="dataset?.params?.createuser?.email"
                >
                  {{ dataset?.params?.createuser?.email }}
                </td>
                <!--
                  Published on / Published by : pendant un workflow en cours
                  ( pendingWorkflows[dataset.id] set ) on cache la valeur
                  pour eviter d'afficher la date/user de la publication
                  PRECEDENTE pendant que l'optimistic toggle inverse le flag .
                  Coherence visuelle : ces colonnes ne refletent que l'etat
                  backend reel post-Phase-2 ( apres syncCurrentDatasetParams ) .
                -->
                <td
                  v-if="pendingWorkflows[dataset.id]"
                  class="pending-placeholder"
                >—</td>
                <td
                  v-else-if="dataset.params.published"
                  :aria-label="formatDate(dataset.params.publisheddate)"
                >
                  {{ formatDate(dataset.params.publisheddate) }}
                </td>
                <td v-else />
                <td
                  v-if="pendingWorkflows[dataset.id]"
                  class="pending-placeholder"
                >—</td>
                <td
                  v-else-if="dataset.params.published"
                  :aria-label="dataset?.params?.publisheduser?.email"
                >
                  {{ dataset?.params?.publisheduser?.email }}
                </td>
                <td v-else />
                <td>
                  <b-field>
                    <template v-if="isLinked(dataset)">
                      <b-tooltip
                        :label="$t('dataVersioning.info-unpublish')"
                        position="is-left"
                      >
                        <b-button
                          class="btnAction"
                          disabled="true"
                          :icon-right="dataset.params.published ? 'check-circle' : 'circle'"
                          size="is-medium"
                          type="is-primary is-light"
                        />
                      </b-tooltip>
                    </template>
                    <template v-else>
                      <!-- Pending workflow ( publish / unpublish ) : la pastille de la
                           colonne Publication bascule en spinner anime + label inline
                           " publication en cours " / " depublication en cours " . Le bouton
                           cancel apparait dans la colonne Actions . Reactivite portee
                           par pendingWorkflows[dataset.id] : set par confirmAndPublish ,
                           clear par startPollPendingWorkflow au passage en etat terminal . -->
                      <template v-if="pendingWorkflows[dataset.id]">
                        <b-tooltip
                          :label="cancellingDatasets.has(dataset.id)
                            ? $t('dataVersioning.cancelling-in-progress')
                            : (pendingWorkflows[dataset.id].action === 'PUBLISH'
                                ? $t('dataVersioning.publishing-in-progress')
                                : $t('dataVersioning.unpublishing-in-progress'))"
                          position="is-left"
                        >
                          <b-button
                            class="btnAction pending-spinner"
                            icon-right="spinner"
                            size="is-medium"
                            type="is-info is-light"
                            disabled
                          />
                        </b-tooltip>
                        <span class="pending-action-label">
                          {{ cancellingDatasets.has(dataset.id)
                              ? $t('dataVersioning.cancelling-now')
                              : (pendingWorkflows[dataset.id].action === 'PUBLISH'
                                  ? $t('dataVersioning.publishing-now')
                                  : $t('dataVersioning.unpublishing-now')) }}
                        </span>
                        <!-- C12 : mini barre de progression visible des que cascade Phase 2
                             emet un heartbeat avec recordsProcessed > 0 . Cachee pendant
                             cancel pour ne pas brouiller le label " annulation en cours " . -->
                        <div
                          v-if="!cancellingDatasets.has(dataset.id)
                            && pendingWorkflows[dataset.id].progressPercentage != null"
                          class="pending-progress"
                        >
                          <div class="pending-progress-bar">
                            <div
                              class="pending-progress-fill"
                              :style="{ width: pendingWorkflows[dataset.id].progressPercentage + '%' }"
                            />
                          </div>
                          <span class="pending-progress-label">
                            {{ Math.round(pendingWorkflows[dataset.id].progressPercentage || 0) }}%
                          </span>
                        </div>
                      </template>
                      <b-button
                        v-else
                        class="btnAction"
                        :icon-right="dataset.params.published ? 'check-circle' : 'circle'"
                        size="is-medium"
                        type="is-primary is-light"
                        @click="confirmAndPublish(dataset, !dataset.params.published)"
                      />
                    </template>
                  </b-field>
                </td>
                <td>
                  <b-field>
                    <template v-if="isLinked(dataset)">
                      <b-tooltip
                        :label="$t('dataVersioning.info-delete')"
                        position="is-left"
                      >
                        <b-button
                          class="btnAction"
                          disabled
                          icon-right="times-circle"
                          size="is-medium"
                          type="is-danger is-light"
                        />
                      </b-tooltip>
                      <b-button
                        class="btnAction"
                        icon-right="download"
                        size="is-medium"
                        type="is-primary is-light"
                        @click="downloadDataTypeVersion(dataset)"
                      />
                    </template>
                    <template v-else>
                      <!-- Pendant un workflow en cours : cancel remplace le delete .
                           Eviter d'autoriser un delete sur un fichier dont la Phase 2
                           ( publish / unpublish ) n'est pas terminee : etat incoherent
                           sur referencevalue garanti . -->
                      <b-tooltip
                        v-if="pendingWorkflows[dataset.id]"
                        :label="$t('dataVersioning.cancel-pending')"
                        position="is-left"
                      >
                        <b-button
                          class="btnAction"
                          icon-right="ban"
                          size="is-medium"
                          type="is-warning is-light"
                          :disabled="cancellingDatasets.has(dataset.id)"
                          @click="cancelPendingWorkflow(dataset)"
                        />
                      </b-tooltip>
                      <b-tooltip
                        v-else
                        :label="$t('dataTypesRepository.table-file-data-delete')"
                        :aria-label="$t('dataTypesRepository.table-file-data-delete')"
                        position="is-left"
                      >
                        <b-button
                          class="btnAction"
                          icon-right="times-circle"
                          size="is-medium"
                          type="is-danger is-light"
                          @click="remove(dataset, dataset.params.published)"
                        />
                      </b-tooltip>
                      <b-tooltip
                        :label="$t('dataTypesRepository.table-file-data-download')"
                        :aria-label="$t('dataTypesRepository.table-file-data-download')"
                        position="is-left"
                      >
                        <b-button
                          class="btnAction"
                          icon-right="download"
                          size="is-medium"
                          type="is-primary is-light"
                          @click="downloadDataTypeVersion(dataset)"
                        />
                      </b-tooltip>
                    </template>
                  </b-field>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <b-notification
        v-else-if="(submissionListVisible || !hasAuthorizationScopes) &&
          (referenceScopes[dataId] &&
            Object.keys(requiredAuthorizationsLabels).length === referenceScopes[dataId].length) &&
          !loading "
        type="is-info is-light"
        size="is-small"
        :closable="false"
        class="truncated-banner border-notif-info-light"
      >
        <span
          class="column no-break"
          v-html="$t('dataTypesRepository.no-file-for-scope')"
        />
      </b-notification>
    </div>
  </PageView>
</template>

<script lang="ts">
import PageView from "@/views/common/PageView.vue";
import SubMenu from "@/components/common/SubMenu.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { BinaryFileDataset } from "@/model/file/BinaryFileDataset";
import { FileOrUUID } from "@/model/file/FileOrUUID";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import DatePatternInput from "@/components/forms/DatePatternInput.vue";
import useObject from "@/composable/components/object";
import useArray from "@/composable/components/array";
import useBoolean from "@/composable/components/boolean";
import services from "@/composable/services";
import { onMounted, onBeforeUnmount, provide, ref, inject, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { datePatternLang } from "@/composable/application/datePattern";
import { Dataset } from "@/model/file/Dataset";
import { FileService } from "@/services/rest/FileService";
import ShowErrors from "@/components/application/ShowErrors.vue";
import { dataLoader } from "@/composable/data/dataLoader";
import { ReferenceScope } from "@/model/authorization/ReferenceScope";
import CollapseMenuOneSelect from "@/components/common/CollapseMenuOneSelect.vue";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import CardAccessSectionApplication from "@/components/application/CardAccessSectionApplication.vue";
import {
  BButton,
  BCollapse,
  BDropdown,
  BField,
  BIcon,
  BInput, BMessage, BNotification, BPagination,
  BStepItem,
  BSteps,
  BTag,
  BTaginput,
  BTooltip,
  BUpload,
} from "buefy";
import "@/style/views/data/DataVersioningView.scss";
import i18n from "@/i18n";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { BinaryFileResult, DataRowResult, GetDataResult } from "@/types/generated/UsedGeneratedTypes";
import { ErrorType } from "@/composable/application/errors";

export default {
  name: "DataVersioningView",
  components: {
    BPagination,
    BNotification,
    BMessage,
    CardAccessSectionApplication,
    BTooltip,
    BTaginput,
    BDropdown,
    BInput,
    BField,
    BButton,
    BTag,
    BUpload,
    BStepItem,
    BSteps,
    BIcon,
    BCollapse,
    TitleAndDescription,
    CollapseMenuOneSelect,
    ShowErrors,
    LoadingAnimate,
    PageView,
    SubMenu,
    DatePatternInput,
  },
  props: {
    applicationName: {
      type: String,
      required: true,
    },
    dataId: {
      type: String,
      required: true,
    },
  },
  emits: ["file-changed"],
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const { startUpload, onUploadComplete, onUploadProgress, finishUpload } = inject("fileUpload") as Record<string, (...args: unknown[]) => unknown>;
    const activeStep = ref(0);
    const loader = dataLoader(services);
    provide("reference:dataLoader", loader);
    const locale: string | null = services.internationalisationService.getLocale();
    const { getValueDisplay } = loader;
    const { reactiveObject: references, doChangeObject: changeReferences } = useObject();
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const {
      shallowRefArray: errorsList,
      doChangeArray: changeErrorsList,
      doAddToArray: addErrorsList,
    } = useArray();
    const { reactiveObject: application, doChangeObject: changeApplication } = useObject(
      new LocalApplicationResult(),
    );
    const { reactiveObject: configuration, doChangeObject: changeConfiguration } = useObject();
    const { reactiveObject: referenceScopes, doChangeObject: changeReferenceScopes } = useObject();
    const { shallowRefArray: authorizations, doChangeArray: changeAuthorizations } = useArray();
    const { reactiveObject: versioning, doChangeObject: changeVersioning } = useObject();
    const {
      shallowRefArray: errorsMessages,
      doChangeArray: changeErrorsMessages,
      doAddToArray: addErrorsMessages,
    } = useArray();
    const { reactiveObject: selected, doChangeObject: changeSelected } = useObject();
    const { refBoolean: loading, doChangeBoolean: changeLoading } = useBoolean();
    const { refBoolean: submissionListVisible } = useBoolean();
    const {
      reactiveObject: requiredAuthorizationsObject,
    } = useObject();
    const {
      reactiveObject: requiredAuthorizationsLabels,
    } = useObject();
    let getSubmissionReferenceScopePath
      = services.internationalisationService.getSubmissionReferenceScopePath;
    let getDataTypeDisplay = services.internationalisationService.getDataTypeDisplay;
    const { refBoolean: isLoading, doChangeBoolean: changeIsLoading } = useBoolean();
    const { refBoolean: isWithoutReferenceScopes, doChangeBoolean: changeIsWithoutReferenceScopes } = useBoolean();
    const { shallowRefArray: listReferenceScopeName, doAddToArray: doAddToListReferenceScopeName } = useArray();
    let datasets: any = ref({});
    let file: any = ref(null);
    let startDate = ref(null);
    let endDate = ref(null);
    let comment = ref("");
    // NewObject = [BinaryFileResult + period]
    let currentDataset = ref<BinaryFileResult[]>([]);
    let componentNameTimeScope = "";
    let loadedReferences: any = {};
    let patternDateTimeScope = ref(null);
    onMounted(async () => {
      changeIsLoading(true);
      await init();
      changeSubMenuPaths([
        new SubMenuPath(
          application?.localRefName?.toLowerCase() || props?.dataId?.toLowerCase(),
          () => {
          },
          () => router.push(`/applications/${props.applicationName}/dataTypes`),
        ),
      ]);
      if (!hasAuthorizationScopes.value) {
        updateDatasets();
      }
      changeIsLoading(false);
      // Fix P0-FRONT-3 : restaurer polling pour workflows en cours
      // recuperes via sessionStorage ( reload F5 pendant Phase 2 ) .
      // Premier tick immediat dans startPollPendingWorkflow va verifier
      // l'etat backend ; si deja terminal -> auto-cleanup .
      for (const [datasetId, info] of Object.entries(pendingWorkflows.value)) {
        startPollPendingWorkflow(datasetId, info.correlationId);
      }
    });

    // Fix P0-FRONT-1 : cleanup setInterval polling au demount de la vue .
    // Sans ce hook , chaque navigation hors-view pendant un workflow en cours
    // laisse les setInterval orphelins ( fuite memoire + CPU + 401 spam si JWT
    // expire ) . pollHandles est la source de verite , clear + reset pendingWorkflows
    // et cancellingDatasets pour eviter qu'un retour ulterieur ne reactive le polling .
    onBeforeUnmount(() => {
      for (const handle of pollHandles.values()) {
        window.clearInterval(handle);
      }
      pollHandles.clear();
      pendingWorkflows.value = {};
      cancellingDatasets.value = new Set();
    });
    const { refBoolean: hasAuthorizationScopes } = useBoolean();
    const { refBoolean: hasTimeScope } = useBoolean();
    const currentPage = ref(1);
    const perPage = ref(5);
    const paginatedDatasets = computed(() => {
      const entries = Object.entries(datasets.value || {});
      const start = (currentPage.value - 1) * perPage.value;
      return entries.slice(start, start + perPage.value);
    });
    const totalDatasets = computed(() => {
      return Object.keys(datasets.value || {}).length;
    });

    function updateDate(name: string, value: any) {
      if (hasTimeScope.value) {
        if ("startDate" === name) {
          startDate.value = value;
        }
        else if ("endDate" === name) {
          endDate.value = value;
        }
      }
    }

    function isLinked(dataset: BinaryFileResult) {
      // Optimisation B / scaling 900M : utilise le boolean leger hasLinks
      // retourne par le backend . Fallback sur l'ancien shape pour les
      // payloads issus d'un backend ancien ( backward compat ) .
      if (typeof (dataset as any).hasLinks === "boolean") {
        return (dataset as any).hasLinks;
      }
      if (!dataset.referencedFiles) return false;
      return dataset.referencedFiles.some(file =>
        file.referencedBinaryFileIdsByReferencetype
        && Object.values(file.referencedBinaryFileIdsByReferencetype)
          .some(arr => arr && arr.length > 0),
      );
    }

    function changeFile() {
      // P1-FRONT-6 : log retire de la prod . TODO functional dataversioning::changeFile reste pending .
    }

    function changeCss(id: string) {
      const el = document?.getElementById(id)?.querySelector(".tooltip-content") as HTMLElement | null;
      if (!el) return;
      if (el.style.display === "block") el.style.display = "none";
      else el.style.display = "block";
    }

    async function init() {
      try {
        const fetchedApplication = await services.applicationService.getApplication(props.applicationName, [
          "CONFIGURATION",
          "DATATYPE",
          "REFERENCETYPE",
        ]);
        if (fetchedApplication && locale) {
          fetchedApplication.localRefName = fetchedApplication.configuration.i18n.data[props.dataId].i18n.title[locale]
            || props.dataId;
          fetchedApplication.localRefDescription = fetchedApplication.configuration.i18n.data[props.dataId].i18n.description[locale]
            || props.dataId;
          changeApplication(fetchedApplication);
        }
        changeVersioning(application.configuration.dataDescription[props.dataId].submission);
        hasAuthorizationScopes.value
          = versioning?.submissionScope?.referenceScopes
            && versioning?.submissionScope?.referenceScopes?.length !== 0;
        hasTimeScope.value
          = Object.keys(versioning?.submissionScope?.timescope || {})?.length !== 0;
        componentNameTimeScope = versioning?.submissionScope?.timescope?.component;
        changeConfiguration(application.configuration.dataDescription[props.dataId]);
        changeAuthorizations(configuration.submission.submissionScope);
        changeReferences(application.references);
        changeSelected(
          new BinaryFileDataset({
            datatype: props.dataId,
            requiredAuthorizations: {},
            from: "",
            to: "",
            comment: "",
          }),
        );
        let refForAuth: Record<string, GetDataResult> = {};
        let reference = "";
        for (let auth in authorizations.value) {
          if (!Object.keys(authorizations?.value[auth] || {}).includes("component")
            || !Object.keys(authorizations?.value[auth] || {}).includes("reference")) {
            for (let authRef in authorizations.value[auth]) {
              if (authorizations.value[auth][authRef].reference) {
                reference = authorizations.value[auth][authRef].reference;
              }
              else if (authorizations.value[auth][authRef].component) {
                let component = authorizations.value[auth][authRef].component;
                if (configuration.componentDescriptions[component].checker.refType) {
                  reference = configuration.componentDescriptions[component].checker.refType;
                }
                else if (configuration.validations) {
                  // TODO a tester si quand on met le checker références dans la validation c'est bon
                  for (let validationName in configuration.validations) {
                    if (configuration.validations[validationName].columns.contain(component)) {
                      reference = configuration.validations[validationName].checkers.refType;
                    }
                  }
                }
              }
              if (reference !== "") {
                doAddToListReferenceScopeName(reference);
                let ref = await services.dataService.getData(props.applicationName, reference);
                if (ref.referenceScopes && ref.referenceScopes[props.dataId]) {
                  for (let i = 0; ref.referenceScopes[props.dataId].length > i; i++) {
                    /* on change l'objet si la section referenceScope est remplis
                       dans submission sinon on garde l'internationalisation par défaut du référentiel */
                    if (
                      application?.internationalization?.data[props.dataId]?.submissions
                        ?.referenceScopes[ref.referenceScopes[props.dataId][i].id]
                    ) {
                      ref.referenceScopes[props.dataId][i].i18n = {
                        ...ref.referenceScopes[props.dataId][i].i18n,
                        ...application.internationalization.data[props.dataId].submissions
                          .referenceScopes[ref.referenceScopes[props.dataId][i].id],
                      };
                    }
                  }
                }
                loadedReferences[reference] = ref;
                refForAuth[reference] = ref;
                changeReferenceScopes(refForAuth[reference].referenceScopes);
              }
              if (referenceScopes[props.dataId]) {
                sortedReferenceScope(referenceScopes);
              }
              if (!referenceScopes[props.dataId]?.length) {
                changeIsWithoutReferenceScopes(true);
              }
            }
          }
        }
        // format date en fonction du pattern du checker
        if (configuration?.componentDescriptions?.[componentNameTimeScope]?.checker?.pattern) {
          patternDateTimeScope.value
            = configuration.componentDescriptions[componentNameTimeScope].checker.pattern;
        }
        else if (configuration.validations) {
          // TODO a tester si quand on met le checker références dans la validation c'est bon
          for (let validationName in configuration.validations) {
            if (configuration.validations[validationName].columns.contain(componentNameTimeScope)) {
              patternDateTimeScope.value
                = configuration.validations[validationName].checkers.pattern;
            }
          }
        }
        for (const [key, value] of Object.entries(refForAuth)) {
          refForAuth[key] = await partitionReferencesValues(value.rows);
        }
      }
      catch (error) {
        // P1-FRONT-6 : log retire ; toastServerError suffit pour l'utilisateur .
        services.alertService.toastServerError();
      }
    }

    function sortedReferenceScope(referenceScope: any) {
      for (let i = 0; i < referenceScope[props.dataId].length; i++) {
        if (Object.keys(referenceScope[props.dataId][i].nodes).length !== 0) {
          referenceScope[props.dataId][i].nodes = Object.fromEntries(
            Object.entries(referenceScope[props.dataId][i].nodes)
              .sort(([, a], [, b]) => (a as any).naturalKey.localeCompare((b as any).naturalKey))
              .map(([key, value], index) => [index, value, key]),
          );
        }
      }
    }

    async function getOrLoadReferences(reference: string) {
      if (loadedReferences[reference]) {
        return loadedReferences[reference];
      }
      let ref = await services.dataService.getData(props.applicationName, reference);
      references[reference] = ref;
      // app.$set(references, reference, ref);
      loadedReferences[reference] = ref;
      return ref;
    }

    function UTCToString(utcString: string) {
      return utcString && utcString.replace(/(\d{4})-(\d{2})-(\d{2}).*!/, "$3/$2/$1");
    }

    function formatDate(date: string) {
      return datePatternLang(new Date(date).toISOString(), "dd/MM/yyyy HH:mm");
    }

    /**
     * Selectionne une periode dans la table "List of datasets on this repository" .
     * Pilote 2 choses :
     * <ul>
     *   <li>Le surlignage CSS de la ligne via {@link selectedPeriode}</li>
     *   <li>Le contenu de la table inferieure "List of versions for the period"
     *       via {@link currentDataset} ( tri par date de creation ascendante )</li>
     * </ul>
     *
     * @param dataset Dataset contenant les versions ( BinaryFileResult ) a afficher
     * @param periode cle de periode au format "du {from} au {to}" ou "" si pas de
     *                timescope ; null efface la selection
     */
    function showDatasets(dataset: Dataset, periode: string | null = null): void {
      selectedPeriode.value = periode;
      if (!dataset) {
        currentDataset.value = [];
        return;
      }
      currentDataset.value = dataset.datasets?.sort(
        (d1: BinaryFileResult, d2: BinaryFileResult) =>
          d1.params.createdate < d2.params.createdate ? -1
            : d1.params.createdate > d2.params.createdate ? 1 : 0,
      );
    }

    async function upload() {
      changeLoading(true);
      services.alertService.info(
        i18n.global.t("dataVersioning.modal-dialog-information.title"),
        i18n.global.t("dataVersioning.modal-dialog-information.message"),
        i18n.global.t("dataVersioning.modal-dialog-information.btn-validate"),
        i18n.global.t("dataVersioning.modal-dialog-information.btn-delete"),
      );
      try {
        startUpload();
        if (file.value) {
          const fileOrId = new FileOrUUID(
            null,
            new BinaryFileDataset(
              props.dataId,
              selected.requiredAuthorizations,
              startDate.value,
              endDate.value,
              comment.value,
            ),
            false,
          );
          const uuid = await services.dataService.addData(
            props.applicationName,
            props.dataId,
            file.value,
            fileOrId,
            onUploadProgress,
            onUploadComplete,
          );
          await updateDatasets(uuid.id);
        }
      }
      finally {
        changeLoading(false);
        activeStep.value = 0;
        file.value = null;
        comment.value = "";
        if (hasTimeScope) {
          startDate.value = null;
          endDate.value = null
        }
        finishUpload();
      }
    }

    /**
     * Workflows publish / unpublish actuellement en cours pour un dataset donne .
     *
     * <ul>
     *   <li>Cle : {@code dataset.id} ( UUID du BinaryFile )</li>
     *   <li>Valeur : {@code { correlationId , action }} - action permet a la UI
     *       d'afficher " publication en cours " vs " depublication en cours "
     *       sans deviner via l'etat published .</li>
     * </ul>
     *
     * <p>Source de verite pour le rendu spinner + cancel + label inline .
     * Set par {@link executePublishToggle} apres Phase 1 OK , clear par
     * {@link clearPollFor} a la detection d'etat terminal .
     */
    type PendingAction = "PUBLISH" | "UNPUBLISH";
    interface PendingInfo {
      correlationId: string;
      action: PendingAction;
      // C12 : progress % live ( null tant que cascade Phase 2 n'a pas encore
      // emis de heartbeat avec recordsProcessed > 0 ) . Mis a jour a chaque
      // tick polling 3 s par startPollPendingWorkflow .
      progressPercentage?: number | null;
    }

    /**
     * Fix P0-FRONT-3 : persistance sessionStorage de pendingWorkflows pour
     * survivre a un reload F5 pendant Phase 2 backend . Cle scopee par
     * applicationName + dataId pour eviter cross-talk entre vues / apps .
     * sessionStorage = vie navigateur tab ( OK pour cas usage ) , pas
     * localStorage qui survivrait au logout .
     */
    const PENDING_STORAGE_KEY = `oa.pendingWorkflows.${props.applicationName}.${props.dataId}`;

    function loadPendingFromStorage(): Record<string, PendingInfo> {
      // A REVOIR : version de Lucile ci-dessous ( merge develop 9ea936e9 ) ,
      // conflit avec la persistance sessionStorage + types PendingInfo .
      // async function publish(dataset: BinaryFileResult, published: boolean) {
      //   changeLoading(true);
      //   services.alertService.info(
      //     i18n.global.t("dataVersioning.modal-dialog-information.title"),
      //     i18n.global.t("dataVersioning.modal-dialog-information.message"),
      //     i18n.global.t("dataVersioning.modal-dialog-information.btn-validate"),
      //     i18n.global.t("dataVersioning.modal-dialog-information.btn-delete"),
      //     1200,
      //   );
      //   startUpload(false);
      //   changeErrorsMessages([]);
      //   dataset.params.published = published;
      //   let requiredAuthorizations = dataset.params.binaryFileDataset.requiredAuthorizations;
      //   requiredAuthorizations = Object.keys(requiredAuthorizations).reduce(function (acc, key) {
      //     acc[key] = acc[key] ? acc[key].sql : "";
      //     return acc;
      //   }, requiredAuthorizations);
      //   dataset.params.binaryFileDataset.requiredAuthorizations = requiredAuthorizations;
      //   const fileOrId = new FileOrUUID(dataset.id, dataset.params.binaryFileDataset, published);
      // ( fin partie commentee a revoir )
      try {
        const raw = window.sessionStorage.getItem(PENDING_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, PendingInfo>;
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }

    function savePendingToStorage(state: Record<string, PendingInfo>): void {
      try {
        if (Object.keys(state).length === 0) {
          window.sessionStorage.removeItem(PENDING_STORAGE_KEY);
        } else {
          window.sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(state));
        }
      } catch { /* quota ou private mode : best-effort */ }
    }

    const pendingWorkflows = ref<Record<string, PendingInfo>>(loadPendingFromStorage());
    const pollHandles = new Map<string, number>();

    /**
     * Periode actuellement selectionnee dans la table "List of datasets on
     * this repository" . Pilote le surlignage CSS {@code is-selected-row}
     * et le rendu de la table inferieure "List of versions for the period" .
     * {@code null} = aucune ligne selectionnee ( etat initial ) .
     */
    const selectedPeriode = ref<string | null>(null);

    function clearPollFor(datasetId: string) {
      const h = pollHandles.get(datasetId);
      if (h != null) {
        window.clearInterval(h);
        pollHandles.delete(datasetId);
      }
      const copy = { ...pendingWorkflows.value };
      delete copy[datasetId];
      pendingWorkflows.value = copy;
      savePendingToStorage(copy);
      // Cleanup cancellingDatasets en meme temps : si la transition terminale
      // est CANCELLED , l'utilisateur a clique cancel et le flag etait set ; le
      // retirer ici sync avec la disparition du spinner ( evite leak du Set ) .
      if (cancellingDatasets.value.has(datasetId)) {
        const next = new Set(cancellingDatasets.value);
        next.delete(datasetId);
        cancellingDatasets.value = next;
      }
    }

    /**
     * Polling 3 s sur le status workflow ; sortie sur etat terminal .
     * Reactive : tant qu'un workflow publish / unpublish / delete tourne
     * pour ce dataset , le spinner + bouton cancel restent visibles dans
     * le template via {@link pendingWorkflows} . Des qu'on detecte un
     * status terminal ( COMPLETED / FAILED / CANCELLED ) , clearPollFor
     * arrete le setInterval pour ce dataset -> plus aucun polling actif
     * tant qu'aucun nouveau workflow n'est lance .
     */
    const POLL_INTERVAL_MS = 3000;

    /**
     * Apres le refetch silencieux post-Phase-2 , {@link getDatasetMap} reconstruit
     * {@code datasets.value} avec de NOUVEAUX objets Dataset / BinaryFileResult ,
     * mais {@code currentDataset.value} pointe toujours sur les items pre-Phase-2 .
     * Le binding {@code v-for="(dataset, index) in currentDataset"} continuerait
     * donc d'afficher l'ancien {@code params.published} . On patch les items
     * en place ( merge {@code params} ) , preservant la reference du tableau
     * pour que Vue conserve les keys + ne reflow pas le DOM .
     */
    function syncCurrentDatasetParams(datasetId: string): void {
      if (!currentDataset.value?.length) return;
      const target = currentDataset.value.find((d: BinaryFileResult) => d.id === datasetId);
      if (!target) return;
      for (const dsKey in datasets.value) {
        const arr = (datasets.value as Record<string, { datasets?: BinaryFileResult[] }>)[dsKey]?.datasets || [];
        const fresh = arr.find((d: BinaryFileResult) => d.id === datasetId);
        if (fresh) {
          target.params = { ...fresh.params };
          return;
        }
      }
    }

    /**
     * P0 cancel-divergence fix : source de verite UI = {@code binaryfile.params}
     * en BDD , lu via l'endpoint dedie {@code /file/{id}/publication-state} .
     * Appele en post-terminal d'un workflow publish/unpublish/cancel pour
     * garantir que l'UI reflete l'etat reel commite , independemment du
     * statut workflow_log ( qui peut diverger en cas de race condition ) .
     *
     * <p>Patch in-place les params {@code published} + {@code publisheddate}
     * + {@code publisheduser.login} dans {@code currentDataset} ET
     * {@code datasets.value} pour reactivite immediate sans refetch lourd .
     * Best-effort : si l'endpoint echoue ( reseau , 502 ) , on garde l'etat
     * obtenu par le refetch {@code /filesOnRepository} en amont ( fallback ) .
     */
    async function applyAuthoritativePublicationState(datasetId: string): Promise<void> {
      try {
        const state = await FileService.INSTANCE.getPublicationState(props.applicationName, datasetId);
        // publisheduser cote BinaryFileInfosResult = UserDescriptionResult
        // { id , login , email } . Depuis l'endpoint leger on n'a que le
        // login ; on fabrique un UserDescriptionResult minimal ( id/email
        // vides ) - suffisant pour l'affichage UI qui montre uniquement
        // le login dans la table .
        const userObj = state.publishedByLogin
          ? { id: "", login: state.publishedByLogin, email: "" }
          : { id: "", login: "", email: "" };
        const patch = (item: BinaryFileResult | undefined) => {
          if (!item || !item.params) return;
          item.params = {
            ...item.params,
            published: state.published,
            publisheddate: state.publishedOn ?? "",
            publisheduser: userObj,
          };
        };
        patch(currentDataset.value?.find((d: BinaryFileResult) => d.id === datasetId));
        for (const dsKey in datasets.value) {
          const arr = (datasets.value as Record<string, { datasets?: BinaryFileResult[] }>)[dsKey]?.datasets || [];
          patch(arr.find((d: BinaryFileResult) => d.id === datasetId));
        }
      } catch {
        /* fallback silencieux sur le refetch /filesOnRepository deja effectue */
      }
    }

    function startPollPendingWorkflow(datasetId: string, correlationId: string) {
      // Defensive : if a stale handle is registered ( e.g . unmount didn't
      // clear properly , or remount before a previous tick resolved ) , we
      // overwrite it after explicit clear . Otherwise the new poll would
      // be skipped silently and the spinner could stay forever ( B1 audit ) .
      const stale = pollHandles.get(datasetId);
      if (stale != null) { window.clearInterval(stale); pollHandles.delete(datasetId); }
      // Fix P1-FRONT-5 : guard contre double terminal toast quand tick HTTP
      // depasse POLL_INTERVAL_MS et 2 ticks concurrents resolvent en meme temps .
      let terminated = false;
      // Transient error retry (B2 audit) : a single 502 / 503 / network blip
      // during cascade restart used to instantly mark the workflow terminal
      // and refetch , confusing the user . We tolerate up to 3 consecutive
      // errors before considering the workflow effectively disappeared .
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 3;
      const tick = async () => {
        if (terminated) return;
        try {
          const status = await services.dataService.getWorkflowStatus(correlationId);
          consecutiveErrors = 0;
          // P0-6 cancel-divergence fix : treat UNKNOWN as terminal-error .
          // If backend returns malformed summary , the previous logic looped
          // every 3 s indefinitely - spinner stuck while reality was either
          // success or failure . Surfacing terminal-error triggers cleanup +
          // refetch , which re-reads binaryfile.params.published as ground
          // truth for the UI rather than relying on a stale polling response .
          const isTerminal = status.status === "COMPLETED"
                          || status.status === "FAILED"
                          || status.status === "CANCELLED"
                          || status.status === "UNKNOWN";
          if (!isTerminal) {
            // C12 : refresh progress % in-place ( reactif via copy + reassign
            // pour declencher le rerender de la mini-barre dans le template ) .
            const current = pendingWorkflows.value[datasetId];
            if (current && current.progressPercentage !== status.progressPercentage) {
              pendingWorkflows.value = {
                ...pendingWorkflows.value,
                [datasetId]: { ...current, progressPercentage: status.progressPercentage ?? null },
              };
            }
            return;
          }
          terminated = true;
          clearPollFor(datasetId);
          try {
            // FileService /filesOnRepository sit derriere un ETag cache JS-side .
            // Si on ne l'invalide pas , la requete suivante peut renvoyer un 304 +
            // payload cache obsolete pre-publish ( params.published encore false ,
            // publisheddate / publisheduser absents ) , et l'UI continue d'afficher
            // " No publication " malgre la transition COMPLETED .
            FileService.clearFilesOnRepositoryEtagCache();
            await updateDatasets(datasetId, { silent: true });
            syncCurrentDatasetParams(datasetId);
          } catch { /* refetch best-effort */ }
          // P0 cancel-divergence fix : override par la source de verite
          // ( binaryfile.params en DB ) . Garantit que l'UI reflete l'etat
          // REEL committe , independemment du statut workflow_log qui peut
          // diverger en cas de race ( ex sweeper marque CANCELLED tandis
          // que Phase 2 commit late binaryfile.published=true ) .
          await applyAuthoritativePublicationState(datasetId);
          notifyWorkflowTerminal(status.status, status.fatalError ?? undefined);
        } catch (e) {
          consecutiveErrors += 1;
          if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS) return; // tolerate blip
          if (terminated) return;
          terminated = true;
          // Workflow disparu ( 404 ) ou backend down trop longtemps ; cleanup
          // optimiste apres N erreurs consecutives pour eviter spinner
          // perpetuel quand le workflow_log row est purge ou inaccessible .
          clearPollFor(datasetId);
          try {
            // P0-5 cancel-divergence fix : ETag cache MUST be invalidated on
            // error-path cleanup too , not just the happy-path . Without this ,
            // when the workflow disappears ( 404 after backend purge or
            // post-cancel race ) , the subsequent refetch returns the stale
            // pre-publish snapshot via 304 + cached payload , and the UI shows
            // "Publishing" forever ( the symptom reported in the divergence
            // incident "presumed dead vs published" ) . Clearing ETag forces
            // a full refetch that reflects the real binaryfile.params state .
            FileService.clearFilesOnRepositoryEtagCache();
            await updateDatasets(datasetId, { silent: true });
            syncCurrentDatasetParams(datasetId);
          } catch { /* refetch best-effort */ }
          // Meme garantie source-de-verite que le happy-path : interroge
          // /publication-state pour patcher l'etat reel sans depender du
          // succes du refetch /filesOnRepository ( workflow_log peut etre
          // purge ou inaccessible , l'endpoint binaryfile reste fonctionnel ) .
          await applyAuthoritativePublicationState(datasetId);
        }
      };
      pollHandles.set(datasetId, window.setInterval(tick, POLL_INTERVAL_MS));
      // Premier tick immediat pour reactivite ( evite 3 s de latence ) .
      tick();
    }

    function notifyWorkflowTerminal(status: string, fatalError?: string): void {
      if (status === "COMPLETED") {
        services.alertService.toastSuccess(t("dataVersioning.publish-completed"));
      } else if (status === "FAILED") {
        services.alertService.toastError(t("dataVersioning.publish-failed") + " : " + (fatalError || ""));
      } else {
        services.alertService.info(
          t("dataVersioning.publish-cancelled-title"),
          t("dataVersioning.publish-cancelled-message"),
          t("dataVersioning.confirm-yes"),
          "",
          1500,
        );
      }
    }

    /**
     * Annule un workflow publish/unpublish en cours pour un dataset .
     * Backend marque {@code workflow_log} en CANCELLED + propage cancellation
     * au handler Phase 2 ( ChunkCancellationRegistry + PublishLifecycleCoordinator ) .
     * Le polling detectera l'etat terminal au prochain tick et fera refetch .
     */
    /**
     * Set des datasetIds avec un appel cancel en vol . Sert a desactiver
     * visuellement + fonctionnellement le bouton cancel pendant la requete
     * pour empecher les double-clicks ( cause de 404 sur la seconde requete
     * quand la premiere a deja transitionne le workflow vers CANCELLED ) .
     */
    const cancellingDatasets = ref<Set<string>>(new Set());

    async function cancelPendingWorkflow(dataset: BinaryFileResult): Promise<void> {
      const info = pendingWorkflows.value[dataset.id];
      if (!info) return;
      if (cancellingDatasets.value.has(dataset.id)) return; // anti double-click
      // Set cancellingDatasets AVANT l'appel HTTP : label "Annulation en cours"
      // visible IMMEDIATEMENT au click + reste actif jusqu'a la detection
      // terminale par le polling ( clearPollFor le retirera ) . Sans ce
      // comportement , le label revenait a "Publication en cours" entre la
      // fin du DELETE et le prochain tick polling -> incoherent pour l'user .
      cancellingDatasets.value = new Set(cancellingDatasets.value).add(dataset.id);
      try {
        await services.dataService.cancelWorkflow(info.correlationId);
        services.alertService.toastSuccess(t("dataVersioning.cancel-requested"));
      } catch {
        services.alertService.toastError(t("dataVersioning.cancel-failed"));
        // En cas d'echec , relacher le flag pour permettre un retry .
        const next = new Set(cancellingDatasets.value);
        next.delete(dataset.id);
        cancellingDatasets.value = next;
      }
      // Le polling continue ; la transition CANCELLED sera detectee + refetch + cleanup
      // via clearPollFor qui retirera AUSSI l'entree cancellingDatasets .
    }

    /**
     * Entree publique appelee par le template au click sur la pastille .
     * Ouvre la modale de confirmation puis delegue a {@link executePublishToggle} .
     *
     * <p><b>Cas supersedure</b> : si un workflow tourne deja pour ce dataset
     * ( {@link pendingWorkflows} contient une entree ) , le message de la
     * modale previent l'utilisateur que l'operation en cours sera annulee
     * avant le lancement de la nouvelle .
     */
    function confirmAndPublish(dataset: BinaryFileResult, published: boolean): void {
      const supersedeInfo = pendingWorkflows.value[dataset.id];
      const supersedeCorrId = supersedeInfo?.correlationId;
      const title = published
        ? t("dataVersioning.confirm-publish-title")
        : t("dataVersioning.confirm-unpublish-title");
      const message = supersedeCorrId
        ? t(published
            ? "dataVersioning.confirm-publish-supersede-message"
            : "dataVersioning.confirm-unpublish-supersede-message")
        : t(published
            ? "dataVersioning.confirm-publish-message"
            : "dataVersioning.confirm-unpublish-message");
      services.alertService.confirm(
        title,
        message,
        t("dataVersioning.confirm-yes"),
        "warning",
        t("dataVersioning.confirm-no"),
        () => { void executePublishToggle(dataset, published, supersedeCorrId); },
      );
    }

    /**
     * Execute le toggle publish / unpublish apres confirmation utilisateur .
     *
     * <ul>
     *   <li>Supersedure : si {@code supersedeCorrId} fourni , cancel best-effort
     *       du workflow en cours avant de lancer le nouveau ;</li>
     *   <li>Pas de {@code loading.value=true} : la table reste visible , seule
     *       la pastille de la row concernee bascule en spinner ( reactivite
     *       portee par {@link pendingWorkflows} ) ;</li>
     *   <li>Optimistic toggle : la pastille reflete l'intention pendant Phase 1
     *       ( ~50 ms HTTP ) , rollback en cas d'echec .</li>
     * </ul>
     */
    async function executePublishToggle(
      dataset: BinaryFileResult,
      published: boolean,
      supersedeCorrId?: string,
    ): Promise<void> {
      if (supersedeCorrId) {
        try {
          await services.dataService.cancelWorkflow(supersedeCorrId);
        } catch {
          // Cancel best-effort : le backend PublishLifecycleService.supersedeIfActive
          // refera l'annulation au moment du nouveau startPublish / startUnpublish .
        }
        clearPollFor(dataset.id);
      }
      changeErrorsMessages([]);
      startUpload(false);
      const previousPublished = dataset.params.published;
      dataset.params.published = published;
      try {
        const result = await services.dataService.togglePublishedFlag(
          props.applicationName, props.dataId, dataset.id, published,
        );
        if (result?.correlationId) {
          const nextPending = {
            ...pendingWorkflows.value,
            [dataset.id]: {
              correlationId: result.correlationId,
              action: published ? "PUBLISH" as const : "UNPUBLISH" as const,
            },
          };
          pendingWorkflows.value = nextPending;
          savePendingToStorage(nextPending);
          startPollPendingWorkflow(dataset.id, result.correlationId);
        }
      } catch (error: unknown) {
        dataset.params.published = previousPublished;
        // Reject 409 Conflict : une operation lifecycle ( PUBLISH / UNPUBLISH /
        // DELETE_FILE ) est deja en cours sur ce fileId . Backend refuse pour
        // eviter d'override / corrompre les donnees ; popup informative
        // ( pas alarmante ) avec contexte clair sur l'operation en cours .
        if (await handleWorkflowAlreadyInProgress(error as ErrorType)) {
          return;
        }
        checkMessageErrors(error as ErrorType);
      } finally {
        finishUpload();
      }
    }

    /**
     * Detecte un HTTP 409 WORKFLOW_ALREADY_IN_PROGRESS et affiche une popup
     * informative claire ( type d'action en cours + login auteur ) plutot
     * qu'un message d'erreur generique . Retourne {@code true} si traite
     * ( pour court-circuiter checkMessageErrors ) .
     */
    async function handleWorkflowAlreadyInProgress(error: ErrorType): Promise<boolean> {
      if (error?.httpResponseCode !== HttpStatusCodes.CONFLICT) return false;
      try {
        const body: any = error.content ? await error.content : null;
        if (body?.code !== "WORKFLOW_ALREADY_IN_PROGRESS") return false;
        const activeType  = String(body.activeWorkflowType || "?");
        const activeLogin = String(body.activeUserLogin    || "?");
        services.alertService.info(
          t("dataVersioning.workflow-already-in-progress-title"),
          t("dataVersioning.workflow-already-in-progress-message", {
            action: activeType,
            user:   activeLogin,
          }),
          t("dataVersioning.confirm-yes"),
          "",
          0,
        );
        return true;
      } catch {
        return false;
      }
      // A REVOIR : version de Lucile ci-dessous ( merge develop 9ea936e9 ) ,
      // conflit avec handleWorkflowAlreadyInProgress + finally finishUpload .
      // changeLoading(false);
      // ( fin partie commentee a revoir )
    }

    function checkMessageErrors(error: ErrorType) {
      let message: any[] = [];
      let exceptionGroovyValidationCheckResult = [];
      if (error.httpResponseCode === HttpStatusCodes.BAD_REQUEST) {
        if (error.content != null) {
          changeErrorsList([]);
          error.content.then((value) => {
            for (let i = 0; i < value.length; i++) {
              if (message.length > 0 && value[i]?.type !== "GroovyValidationCheckResult") {
                if (JSON.stringify(value[i]) !== JSON.stringify(value[i - message.length])) {
                  addErrorsList(value[i]);
                }
                for (let j = 0; j < message.length; j++) {
                  if (!message.includes(value[i].message)) {
                    message.push(value[i].message);
                  }
                }
              }
              else if (
                !message.includes(value[i]?.message)
                && value[i]?.type !== "GroovyValidationCheckResult"
              ) {
                message.push(value[i].message);
                addErrorsList(value[i]);
              }
              else if (
                !message.includes(value[i]?.message)
                && value[i]?.type === "GroovyValidationCheckResult"
              ) {
                if (application?.internationalization?.data[props.dataId]?.exceptions && locale) {
                  message.push(value[i].message);
                  for (let columnError of Object.keys(
                    application.internationalization.data[props.dataId].exceptions,
                  )) {
                    exceptionGroovyValidationCheckResult.push(
                      application.internationalization.data[props.dataId].exceptions[columnError][
                        value[i].message
                      ][locale],
                    );
                  }
                  // P1-FRONT-6 : log retire ; erreurs deja affichees via ShowErrors composant .
                }
              }
            }
            if (errorsList.value.length !== 0) {
              changeErrorsMessages(services.errorsService.getCsvErrorsMessages(errorsList.value));
            }
            else {
              changeErrorsMessages(services.errorsService.getErrorsMessages(error));
            }
            exceptionGroovyValidationCheckResult.forEach(message => addErrorsMessages(message));
          });
        }
      }
      else {
        services.alertService.toastServerError(error as ErrorType);
      }
    }

    async function selectAuthorization(key: string, event: any) {
      selected.requiredAuthorizations[event.type] = [event.naturalKey];
      requiredAuthorizationsLabels[event.type] = [event.localName];
      requiredAuthorizationsObject[event.type] = [event.id];
      /* changeRequiredAuthorizationsObject({
        ...requiredAuthorizationsObject,
        [event.type]: [event.id],
      });
      changeRequiredAuthorizationsLabels({
        ...requiredAuthorizationsLabels,
        [event.type]: [event.localName],
      }); */
      if (currentDataset.value) {
        datasets.value = currentDataset.value;
      }
      else {
        datasets.value = {};
      }
      /* TODO - voir avec philippe à quoi sert cette partie car je ne comprend pas
      const app = getCurrentInstance().appContext.app;
      app.$refs?.[key]?.[0].toggle(); */
      if (isReferenceScopesSelected() || submissionListVisible.value) {
        await updateDatasets();
      }
    }

    function isReferenceScopesSelected() {
      if (!hasAuthorizationScopes.value) {
        return true;
      }
      if (selected && selected.requiredAuthorizations) {
        submissionListVisible.value
          = selected
            && selected.requiredAuthorizations
            && Object.keys(selected.requiredAuthorizations).length !== 0

            && Object.values(selected.requiredAuthorizations).every(v => v?.length);
        return submissionListVisible.value;
      }
      return false;
    }

    /**
     * Refetch des datasets pour le scope courant + reconstruction map .
     *
     * @param uuid    si fourni , ouvre la periode contenant ce file post-fetch
     * @param options {@code silent=true} pour skip le {@code LoadingAnimate}
     *                global ( utilise par le polling post-Phase-2 : la table
     *                doit rester visible pendant le refetch , seul le row
     *                concerne bascule du spinner vers la pastille finale ) .
     *                Default {@code silent=false} ( spinner global affiche
     *                pendant le fetch initial post-upload ) .
     */
    async function updateDatasets(
      uuid?: string,
      options: { silent?: boolean } = {},
    ): Promise<void> {
      if (!isReferenceScopesSelected() && !submissionListVisible.value) return;
      const silent = options.silent === true;
      if (!silent) loading.value = true;
      try {
        const datasetsList = await services.fileService.getFiles(
          props.applicationName,
          props.dataId,
          selected,
        );
        if (!datasetsList || !datasetsList.length) {
          datasets.value = {};
          currentDataset.value = [];
          return;
        }
        getDatasetMap({ binaryFileList: datasetsList, uuid });
      } finally {
        if (!silent) loading.value = false;
      }
    }

    function getDatasetMap(fileList: { binaryFileList: BinaryFileResult[]; uuid?: string }) {
      const datasetMap: Record<string, Dataset> = {};
      for (const binaryFile of fileList.binaryFileList) {
        const binaryFilePeriodeToString = getPeriodeToStringBinaryFile(binaryFile);
        const localCurrentDataset
          = datasetMap[binaryFilePeriodeToString] || new Dataset(binaryFile);
        localCurrentDataset.addDataset(binaryFile);
        datasetMap[binaryFilePeriodeToString] = localCurrentDataset;
      }
      datasets.value = datasetMap;

      // After refetch , re-resolve currentDataset + selectedPeriode :
      //   1 . Si uuid fourni ( hint ) : trouver la periode contenant ce binary file .
      //   2 . Sinon , preserver selectedPeriode actuelle si toujours valide .
      // Sans ce sync , le tableau du bas se vide apres chaque polling terminal car
      // les nouveaux Dataset objects n'etaient plus reconnectes au currentDataset .
      let targetPeriod: string | undefined;
      if (fileList.uuid) {
        targetPeriod = Object.keys(datasetMap).find(
          p => datasetMap[p].datasets.some(b => b.id === fileList.uuid),
        );
      }
      if (!targetPeriod && selectedPeriode.value && datasetMap[selectedPeriode.value]) {
        targetPeriod = selectedPeriode.value;
      }
      // Sort + reassign EVERY refetch to guarantee Vue reactivity ( meme periode ,
      // mais nouveaux objets BinaryFileResult issus du refetch ) . Sans le sort
      // par version , l'ordre des lignes du bas changerait apres chaque republish .
      if (targetPeriod && datasetMap[targetPeriod]) {
        const sorted = [...datasetMap[targetPeriod].datasets].sort(
          (a: BinaryFileResult, b: BinaryFileResult) =>
            (b.params?.binaryFileDataset?.version ?? 0) - (a.params?.binaryFileDataset?.version ?? 0),
        );
        currentDataset.value = sorted;
        selectedPeriode.value = targetPeriod;
      } else if (!fileList.uuid && !selectedPeriode.value) {
        // Pas de hint + pas de selection : initial load , laisser currentDataset
        // vide jusqu'a un clic explicite de l'utilisateur sur une periode .
        currentDataset.value = [];
      }
      // Sinon ( targetPeriod inconnu mais selectedPeriode set ) : on garde l'ancienne
      // valeur de currentDataset pour ne pas faire disparaitre le tableau du bas .
      return datasets.value;
    }

    function getPeriodeToStringBinaryFile(binaryFile: BinaryFileResult): string {
      const binaryFiledataset = binaryFile.params?.binaryFileDataset;
      if (binaryFiledataset && binaryFiledataset.from && binaryFiledataset.to) {
        return (
          "du "
          + datePatternLang(new Date(binaryFiledataset.from).toISOString(), "dd/MM/yyyy HH:mm")
          + " au "
          + datePatternLang(new Date(binaryFiledataset.to).toISOString(), "dd/MM/yyyy HH:mm")
        );
      }
      return "";
    }

    function remove(dataset: BinaryFileResult, isPublished: boolean) {
      services.alertService.confirm(
        i18n.global.t("dataVersioning.modal-dialog.title"),
        (isPublished
          ? i18n.global.t("dataVersioning.modal-dialog.message-for-file-published")
          : i18n.global.t("dataVersioning.modal-dialog.message-default")),
        "Confirmer suppression",
        "warning",
        "Annuler suppression",
        () => deleteFile(dataset.id),
      );
    }

    async function downloadDataTypeVersion(event: any) {
      await services.fileService.download(props.applicationName, event.id);
      return false;
    }

    async function deleteFile(uuid: string) {
      const deleted = await services.fileService.remove(props.applicationName, uuid);
      await updateDatasets(deleted);
    }

    async function partitionReferencesValues(
      referencesValues: DataRowResult[],
      currentPath?: string,
      currentCompleteLocalName?: string,
    ) {
      let returnValues: any = {};
      for (const referenceValue of referencesValues) {
        const previousKeySplit = currentPath ? currentPath.split(".") : [];
        const keys = referenceValue.hierarchicalKey.split(".");
        const referencesInPartitionRefVal: string[] | undefined = referenceValue.hierarchicalKey.split(".");
        let reference: string | undefined;
        if (previousKeySplit.length === keys.length) {
          continue;
        }
        for (let i = 0; i < previousKeySplit.length; i++) {
          keys.shift();
          referencesInPartitionRefVal.shift();
        }
        const key = keys.shift();
        let newCurrentPath = (currentPath ? currentPath + "." : "") + key;

        reference = referencesInPartitionRefVal.shift()?.split("K").shift() ?? "";
        let refValues;
        if (reference && application.data[reference]) {
          refValues = await getOrLoadReferences(reference);
        }
        services.internationalisationService.getUserPrefLocale();
        let localName = refValues.rows.find(

          (r: Record<string, string>) =>
            r.hierarchicalKey === newCurrentPath
            || r.naturalKey === key?.split(/(?<=[a-z])K/)?.pop(),
        );
        // TODO passé par la constante de l'application pour "__display_" plus tard (en attente de réparation de philippe
        let naturalKey = localName.naturalKey;
        localName = getValueDisplay(
          localName,
          application.configuration.applicationDescription.defaultLanguage,
        );
        if (!localName) {
          localName = key;
        }
        let completeLocalName
          = typeof currentCompleteLocalName === "undefined" ? "" : currentCompleteLocalName;
        completeLocalName = completeLocalName + (completeLocalName === "" ? "" : ",") + localName;
        let authPartition = returnValues[naturalKey] || {
          key,
          naturalKey,
          reference,
          rows: [],
          localName,
          isLeaf: false,
          currentPath: newCurrentPath,
          completeLocalName,
        };
        authPartition.rows.push(referenceValue);
        returnValues[naturalKey] = authPartition;
      }
      for (const returnValuesKey in returnValues) {
        const auth = returnValues[returnValuesKey];
        let referenceValueLeaf = auth.rows?.[0];
        if (auth.rows.length <= 1 && referenceValueLeaf.hierarchicalKey === auth.currentPath) {
          returnValues[returnValuesKey] = {
            ...auth,
            isLeaf: true,
            rows: referenceValueLeaf,
          };
        }
        else {
          let r = await partitionReferencesValues(
            auth.rows,
            auth.currentPath,
            auth.completeLocalName,
          );
          returnValues[returnValuesKey] = {
            ...auth,
            isLeaf: false,
            rows: r,
          };
        }
      }
      return returnValues;
    }

    return {
      hasAuthorizationScopes,
      hasTimeScope,
      changeCss,
      getSubmissionReferenceScopePath,
      getDataTypeDisplay,
      changeFile,
      formatDate,
      UTCToString,
      remove,
      confirmAndPublish,
      pendingWorkflows,
      cancellingDatasets,
      selectedPeriode,
      cancelPendingWorkflow,
      upload,
      showDatasets,
      downloadDataTypeVersion,
      selectAuthorization,
      subMenuPaths,
      application,
      locale,
      referenceScopes,
      datasets,
      versioning,
      errorsMessages,
      loading,
      requiredAuthorizationsObject,
      requiredAuthorizationsLabels,
      patternDateTimeScope,
      file,
      startDate,
      endDate,
      updateDate,
      comment,
      currentDataset,
      submissionListVisible,
      startUpload,
      onUploadComplete,
      onUploadProgress,
      finishUpload,
      isLoading,
      isLinked,
      references,
      activeStep,
      listReferenceScopeName,
      isWithoutReferenceScopes,
      currentPage,
      perPage,
      paginatedDatasets,
      totalDatasets,
    };
  },
  computed: {
    ReferenceScope() {
      return ReferenceScope;
    },
    services() {
      return services;
    },
  },
};
</script>
